import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Car,
  Tag,
  Key,
  AlertCircle,
  MessageSquare,
  Search,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LeadboxLayout } from "../components/LeadboxLayout";
import { getStatusConfig } from "@/utils/statusUtils";
import { useVehicleCard } from "@/hooks/useVehicleCard";
import { Pagination } from "@/components/ui/pagination";
import { CreateVehicleCardManuallyDialog } from "@/components/dialogs/CreateVehicleCardManuallyDialog";
import { getLanguage } from "@/utils/languageUtils";
import {
  commonButtonLabels,
  commonTitleLabels,
  commonMiscLabels,
  leadUILabels,
  vehicleCardUILabels,
  vehicleCardStatusLabels,
} from "@/i18n";
import { VehicleCardStatus } from "@/types/enums";

const VehicleCardListPage = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: "",
    status: "all",
  });

  // Separate state for search input (not triggering API calls)
  const [searchInput, setSearchInput] = useState(filters.search);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:vehicleCards",
    10,
    [10, 25, 50],
  );

  // Use vehicle card hook to get data with server-side pagination
  const {
    vehicleCards,
    totalVehicleCards,
    totalPages,
    isLoadingVehicleCards,
    vehicleCardsError,
    refetchVehicleCards,
  } = useVehicleCard({
    search: filters.search,
    status: filters.status === "all" ? undefined : filters.status,
    page: currentPage,
    limit: itemsPerPage,
  });
  // Manual creation dialog state
  const [showCreateManuallyDialog, setShowCreateManuallyDialog] =
    useState(false);

  // Handle search functionality
  const handleSearch = () => {
    // Update the actual search filter with the input value and reset to page 1
    setFilters((prev) => ({ ...prev, search: searchInput }));
    setCurrentPage(1);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setFilters((prev) => ({ ...prev, search: "" }));
    setCurrentPage(1);
  };

  // Handle status change
  const handleStatusChange = (newStatus: string) => {
    setFilters((prev) => ({ ...prev, status: newStatus }));
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <LeadboxLayout>
      <div className="mx-auto max-w-5xl min-w-0 px-3 py-3 md:p-8">
          <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <h1 className="text-xl font-bold md:text-3xl">
              {commonTitleLabels(getLanguage()).vehicleCards}
            </h1>
            <div className="flex flex-wrap items-stretch gap-1.5 sm:items-center sm:gap-2">
              <Button
                onClick={() => refetchVehicleCards()}
                variant="outline"
                size="sm"
                className="backdrop-blur shrink-0 sm:h-10 sm:px-4"
                disabled={isLoadingVehicleCards}
                title={commonButtonLabels(getLanguage()).update}
                aria-label={commonButtonLabels(getLanguage()).update}
              >
                <RefreshCw
                  className={`h-4 w-4 sm:mr-2 ${
                    isLoadingVehicleCards ? "animate-spin" : ""
                  }`}
                />
                <span className="hidden sm:inline">
                  {commonButtonLabels(getLanguage()).update}
                </span>
              </Button>
              <Button
                onClick={() => setShowCreateManuallyDialog(true)}
                size="sm"
                className="min-w-0 flex-1 bg-blue-600 px-2 hover:bg-blue-700 text-white backdrop-blur sm:h-10 sm:flex-initial sm:px-4"
                title={commonButtonLabels(getLanguage()).createVehicleCardManually}
              >
                <Car className="h-4 w-4 shrink-0 sm:mr-2" />
                <span className="truncate sm:whitespace-normal">
                  {commonButtonLabels(getLanguage()).createVehicleCardManually}
                </span>
              </Button>
            </div>
          </div>

          <div className="mb-4 sm:mb-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
              <div className="flex min-w-0 gap-1.5 sm:gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder={
                      commonMiscLabels(getLanguage()).searchPlaceholder
                    }
                    className="pl-10"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                </div>
                <Button
                  onClick={handleSearch}
                  className="backdrop-blur"
                  variant="outline"
                >
                  <Search className="h-4 w-4 mr-2" />
                  {commonMiscLabels(getLanguage()).search}
                </Button>
                {filters.search && (
                  <Button
                    onClick={handleClearSearch}
                    variant="destructive"
                    className="backdrop-blur"
                    size="sm"
                  >
                    {commonMiscLabels(getLanguage()).clear}
                  </Button>
                )}
              </div>

              <Select value={filters.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="bg-background backdrop-blur-md">
                  <SelectValue
                    placeholder={
                      vehicleCardUILabels(getLanguage()).selectStatus
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {commonMiscLabels(getLanguage()).all}
                  </SelectItem>
                  <SelectItem value={VehicleCardStatus.ACTIVE}>
                    {
                      vehicleCardStatusLabels(getLanguage())[
                        VehicleCardStatus.ACTIVE
                      ]
                    }
                  </SelectItem>
                  <SelectItem value={VehicleCardStatus.INACTIVE}>
                    {
                      vehicleCardStatusLabels(getLanguage())[
                        VehicleCardStatus.INACTIVE
                      ]
                    }
                  </SelectItem>
                  <SelectItem value={VehicleCardStatus.COMPLETED}>
                    {
                      vehicleCardStatusLabels(getLanguage())[
                        VehicleCardStatus.COMPLETED
                      ]
                    }
                  </SelectItem>
                  <SelectItem value={VehicleCardStatus.INCOMPLETE}>
                    {
                      vehicleCardStatusLabels(getLanguage())[
                        VehicleCardStatus.INCOMPLETE
                      ]
                    }
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 md:space-y-4">
            {isLoadingVehicleCards ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-blue-500 border-solid"></div>
                <span className="ml-2 text-muted-foreground">
                  {commonMiscLabels(getLanguage()).loading}
                </span>
              </div>
            ) : vehicleCardsError ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <p className="text-red-600 mb-2">
                    {
                      vehicleCardUILabels(getLanguage())
                        .vehicleCardDataNotLoaded
                    }
                  </p>
                  <Button
                    onClick={() => refetchVehicleCards()}
                    variant="outline"
                  >
                    {commonMiscLabels(getLanguage()).tryAgain}
                  </Button>
                </div>
              </div>
            ) : vehicleCards.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-center">
                  <p className="text-muted-foreground">
                    {vehicleCardUILabels(getLanguage()).noVehicleCardsFound}
                  </p>
                </div>
              </div>
            ) : (
              vehicleCards.map((vehicleCard) => (
                <Card
                  key={vehicleCard.id}
                  className="hover:bg-accent/20 backdrop-blur-md cursor-pointer transition-colors"
                  onClick={() =>
                    navigate(`/admin/leadbox/vehicle-cards/${vehicleCard.id}`)
                  }
                >
                  <CardContent className="p-2.5 sm:p-3 md:p-4">
                    <div className="grid w-full grid-cols-2 gap-x-2 gap-y-2 md:grid-cols-5 md:gap-x-4 md:gap-y-0">
                      <div className="flex min-w-0 items-center gap-1.5 md:gap-2">
                        <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground md:h-5 md:w-5" />
                        <div className="min-w-0">
                          <div className="text-[10px] leading-tight text-muted-foreground md:text-sm">
                            {
                              vehicleCardUILabels(getLanguage())
                                .registrationNumber
                            }
                          </div>
                          <div className="truncate text-xs font-semibold md:text-base">
                            {vehicleCard.regNumber}
                          </div>
                        </div>
                      </div>

                      <div className="flex min-w-0 items-center gap-1.5 md:gap-2">
                        <Key className="h-3.5 w-3.5 shrink-0 text-muted-foreground md:h-5 md:w-5" />
                        <div className="min-w-0">
                          <div className="text-[10px] leading-tight text-muted-foreground md:text-sm">
                            {commonMiscLabels(getLanguage()).id}
                          </div>
                          <div className="truncate text-xs font-semibold md:text-base">
                            {vehicleCard.vehicleCardId}
                          </div>
                        </div>
                      </div>

                      <div className="col-span-2 flex min-w-0 items-center gap-1.5 md:col-span-1 md:gap-2">
                        <Car className="h-3.5 w-3.5 shrink-0 text-muted-foreground md:h-5 md:w-5" />
                        <div className="min-w-0">
                          <div className="text-[10px] leading-tight text-muted-foreground md:text-sm">
                            {commonMiscLabels(getLanguage()).car}
                          </div>
                          <div className="truncate text-xs font-semibold md:text-base">
                            {vehicleCard.make} {vehicleCard.model}
                          </div>
                        </div>
                      </div>

                      <div className="flex min-w-0 items-center gap-1.5 md:gap-2">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground md:h-5 md:w-5" />
                        <div className="min-w-0">
                          <div className="text-[10px] leading-tight text-muted-foreground md:text-sm">
                            {vehicleCardUILabels(getLanguage()).status}
                          </div>
                          {(() => {
                            const statusConfig = getStatusConfig(
                              vehicleCard.status
                            );
                            return (
                              <Badge
                                variant={statusConfig.color}
                                className="max-w-full truncate px-1.5 py-0 text-[10px] md:px-2.5 md:py-0.5 md:text-xs"
                              >
                                {statusConfig.label}
                              </Badge>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="flex min-w-0 items-center gap-1.5 md:gap-2">
                        <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground md:h-5 md:w-5" />
                        <div className="min-w-0">
                          <div className="text-[10px] leading-tight text-muted-foreground md:text-sm">
                            {commonMiscLabels(getLanguage()).comments}
                          </div>
                          <div className="truncate text-xs font-semibold md:text-base">
                            {vehicleCard.nrOfComments}{" "}
                            {commonMiscLabels(getLanguage()).pieces}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalVehicleCards}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 25, 50]}
            />
          )}
        </div>

      {/* Manual Creation Dialog */}
      <CreateVehicleCardManuallyDialog
        open={showCreateManuallyDialog}
        onOpenChange={setShowCreateManuallyDialog}
      />
    </LeadboxLayout>
  );
};

export default VehicleCardListPage;
